export const DEFAULT_SHOP_TIMEZONE = "Europe/Tallinn";

export type AdjustmentWeekdayCode =
  | "sun"
  | "mon"
  | "tue"
  | "wed"
  | "thu"
  | "fri"
  | "sat";

export type AdjustmentTimingWeekday = {
  weekday: AdjustmentWeekdayCode | number;
  fromTime: string;
  toTime: string;
  /** @deprecated Legacy loadLocationProducts rows */
  from?: string;
  to?: string;
  active?: boolean;
};

export type AdjustmentTiming = {
  startsDateTime?: string;
  endsDateTime?: string;
  indefinite?: boolean;
  weekdays?: AdjustmentTimingWeekday[];
  /** @deprecated Legacy flat schedule */
  startsAt?: string;
  endsAt?: string;
  starts_at?: string;
  ends_at?: string;
};

export type LocationProductAdjustment = {
  name?: string;
  display_name?: string;
  value: number | string;
  summarizeWithOtherAdjustments?: boolean;
  summarize?: boolean;
  timing?: AdjustmentTiming | null;
  /** @deprecated Legacy flat schedule on the adjustment root */
  startsAt?: string;
  endsAt?: string;
  starts_at?: string;
  ends_at?: string;
  indefinite?: boolean;
  weekdays?: AdjustmentTimingWeekday[];
};

export type LocationProductForPricing = {
  price_regular?: number;
  price_recular_net?: number;
  price_regular_net?: number;
  adjustments?: LocationProductAdjustment[] | null;
};

export type ResolvedLocationProductPricing = {
  activeAdjustments: LocationProductAdjustment[];
  price_total: number;
  price_total_net: number;
  adjustment_amount: number;
  adjustment_amount_net: number;
  hasDiscount: boolean;
};

const WEEKDAY_SHORT_TO_NUMBER: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

const WEEKDAY_CODE_TO_NUMBER: Record<string, number> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

function parseTimeToMinutes(time: string): number {
  const normalized = time.replace(/[zZ]$/, "");
  const [hours, minutes] = normalized.split(":").map(Number);
  if ([hours, minutes].some((v) => Number.isNaN(v))) {
    return 0;
  }
  return hours * 60 + minutes;
}

function weekdayRuleToNumber(weekday: AdjustmentTimingWeekday["weekday"]): number {
  if (typeof weekday === "string") {
    return WEEKDAY_CODE_TO_NUMBER[weekday.toLowerCase()] ?? 0;
  }
  return weekday;
}

function getWeekdayRuleTimes(rule: AdjustmentTimingWeekday): {
  from: string;
  to: string;
} {
  return {
    from: rule.fromTime ?? rule.from ?? "00:00",
    to: rule.toTime ?? rule.to ?? "00:00",
  };
}

export function getAdjustmentDisplayName(
  adj: LocationProductAdjustment
): string {
  return adj.name ?? adj.display_name ?? "";
}

function resolveAdjustmentTiming(
  adj: LocationProductAdjustment
): AdjustmentTiming | null {
  if (adj.timing) {
    return adj.timing;
  }

  if (
    adj.weekdays?.length ||
    adj.startsAt ||
    adj.starts_at ||
    adj.endsAt ||
    adj.ends_at ||
    adj.indefinite === false
  ) {
    return {
      startsDateTime: adj.startsAt ?? adj.starts_at,
      endsDateTime: adj.endsAt ?? adj.ends_at,
      indefinite: adj.indefinite,
      weekdays: adj.weekdays,
    };
  }

  return null;
}

function getTimingBounds(timing: AdjustmentTiming): {
  startsAt?: string;
  endsAt?: string;
} {
  return {
    startsAt:
      timing.startsDateTime ??
      timing.startsAt ??
      timing.starts_at,
    endsAt:
      timing.endsDateTime ?? timing.endsAt ?? timing.ends_at,
  };
}

export function getZonedWeekdayAndMinutes(
  date: Date,
  timeZone: string = DEFAULT_SHOP_TIMEZONE
): { weekday: number; minutesOfDay: number } {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const weekdayStr = parts.find((p) => p.type === "weekday")?.value ?? "Sun";
  const hour = Number(parts.find((p) => p.type === "hour")?.value ?? 0);
  const minute = Number(parts.find((p) => p.type === "minute")?.value ?? 0);

  return {
    weekday: WEEKDAY_SHORT_TO_NUMBER[weekdayStr] ?? 0,
    minutesOfDay: hour * 60 + minute,
  };
}

export function isTimeBasedAdjustment(
  adj: LocationProductAdjustment
): boolean {
  if (adj.timing) {
    return true;
  }
  if (Array.isArray(adj.weekdays)) {
    return true;
  }
  if (adj.indefinite === false) {
    return true;
  }
  if (adj.startsAt ?? adj.starts_at) {
    return true;
  }
  if (adj.endsAt ?? adj.ends_at) {
    return true;
  }
  return false;
}

function matchesWeekdayRule(
  weekday: number,
  ruleWeekday: number
): boolean {
  return ruleWeekday === weekday || (ruleWeekday === 7 && weekday === 0);
}

function isWithinWeekdayWindow(
  weekdays: AdjustmentTimingWeekday[],
  date: Date,
  timeZone: string
): boolean {
  if (!weekdays.length) {
    return false;
  }

  const { weekday, minutesOfDay } = getZonedWeekdayAndMinutes(date, timeZone);
  const dayRule = weekdays.find((w) =>
    matchesWeekdayRule(weekday, weekdayRuleToNumber(w.weekday))
  );

  if (!dayRule || dayRule.active === false) {
    return false;
  }

  const { from, to } = getWeekdayRuleTimes(dayRule);
  const fromMinutes = parseTimeToMinutes(from);
  const toMinutes = parseTimeToMinutes(to);
  return minutesOfDay >= fromMinutes && minutesOfDay <= toMinutes;
}

/**
 * Missing startsDateTime: promotion already started.
 * Missing endsDateTime: no scheduled end (runs indefinitely).
 */
function isWithinDateRange(
  timing: AdjustmentTiming,
  date: Date
): boolean {
  const { startsAt, endsAt } = getTimingBounds(timing);

  if (startsAt) {
    const start = new Date(startsAt);
    if (!Number.isNaN(start.getTime()) && date.getTime() < start.getTime()) {
      return false;
    }
  }

  if (endsAt) {
    const end = new Date(endsAt);
    if (!Number.isNaN(end.getTime()) && date.getTime() > end.getTime()) {
      return false;
    }
  }

  return true;
}

export function isAdjustmentActiveAt(
  adj: LocationProductAdjustment,
  date: Date = new Date(),
  timeZone: string = DEFAULT_SHOP_TIMEZONE
): boolean {
  if (!isTimeBasedAdjustment(adj)) {
    return true;
  }

  const timing = resolveAdjustmentTiming(adj);
  if (!timing) {
    return true;
  }

  if (!timing.indefinite && !isWithinDateRange(timing, date)) {
    return false;
  }

  const weekdays = timing.weekdays;
  if (!weekdays?.length) {
    return true;
  }

  return isWithinWeekdayWindow(weekdays, date, timeZone);
}

export function getActiveAdjustments(
  adjustments: LocationProductAdjustment[] | null | undefined,
  date: Date = new Date(),
  timeZone: string = DEFAULT_SHOP_TIMEZONE
): LocationProductAdjustment[] {
  if (!adjustments?.length) {
    return [];
  }

  return adjustments.filter((adj) => isAdjustmentActiveAt(adj, date, timeZone));
}

function adjustmentSummarizeWithOthers(
  adj: LocationProductAdjustment
): boolean {
  return (
    adj.summarizeWithOtherAdjustments === true || adj.summarize === true
  );
}

export function applyAdjustmentsToPrice(
  regularPrice: number,
  activeAdjustments: LocationProductAdjustment[]
): number {
  if (!activeAdjustments.length) {
    return roundMoney(regularPrice);
  }

  const summarizeGroup = activeAdjustments.filter((adj) =>
    adjustmentSummarizeWithOthers(adj)
  );
  const sequentialGroup = activeAdjustments.filter(
    (adj) => !adjustmentSummarizeWithOthers(adj)
  );

  let price = regularPrice;

  if (summarizeGroup.length > 0) {
    const summedPercent = summarizeGroup.reduce((sum, adj) => {
      const value = Number(adj.value);
      return Number.isFinite(value) ? sum + value : sum;
    }, 0);
    price *= 1 + summedPercent / 100;
  }

  for (const adj of sequentialGroup) {
    const value = Number(adj.value);
    if (!Number.isFinite(value)) {
      continue;
    }
    price *= 1 + value / 100;
  }

  return roundMoney(price);
}

export function resolveLocationProductPricing(
  locationProduct: LocationProductForPricing,
  date: Date = new Date(),
  timeZone: string = DEFAULT_SHOP_TIMEZONE
): ResolvedLocationProductPricing {
  const price_regular = Number(locationProduct.price_regular) || 0;
  const price_regular_net =
    Number(
      locationProduct.price_recular_net ?? locationProduct.price_regular_net
    ) || 0;

  const activeAdjustments = getActiveAdjustments(
    locationProduct.adjustments,
    date,
    timeZone
  );

  const price_total =
    activeAdjustments.length > 0
      ? applyAdjustmentsToPrice(price_regular, activeAdjustments)
      : price_regular;

  const price_total_net =
    activeAdjustments.length > 0
      ? applyAdjustmentsToPrice(price_regular_net, activeAdjustments)
      : price_regular_net;

  const adjustment_amount = roundMoney(price_total - price_regular);
  const adjustment_amount_net = roundMoney(price_total_net - price_regular_net);

  return {
    activeAdjustments,
    price_total,
    price_total_net,
    adjustment_amount,
    adjustment_amount_net,
    hasDiscount:
      activeAdjustments.length > 0 &&
      (adjustment_amount !== 0 || adjustment_amount_net !== 0),
  };
}

export function mergeResolvedPricing<T extends LocationProductForPricing>(
  locationProduct: T,
  date: Date = new Date(),
  timeZone: string = DEFAULT_SHOP_TIMEZONE
): T & ResolvedLocationProductPricing {
  const resolved = resolveLocationProductPricing(
    locationProduct,
    date,
    timeZone
  );

  return {
    ...locationProduct,
    ...resolved,
    adjustments: resolved.activeAdjustments,
  };
}
